import React, { RefObject } from 'react';

import { useClassnames, classnames } from '@lumapps/classnames';
import { mdiClose, mdiMenuDown } from '@lumapps/lumx/icons';
import {
    PopoverProps,
    Popover,
    Placement,
    IconButton,
    Orientation,
    FlexBox,
    Toolbar,
    Emphasis,
    IconButtonProps,
} from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { USER_CARD_POPOVER_CLASSNAME, USER_CARD_POPOVER_CLOSE_ELEMENT } from '../../constants';
import { isConnected } from '../../ducks/selectors';
import { useUser } from '../../hooks/useUser';
import { USER } from '../../keys';
import { MinimumViableUser } from '../../types';
import { UserCard, UserCardProps } from '../UserCard';

import './index.scss';

export interface UserCardPopoverProps extends UserCardProps {
    /** user to display */
    user: MinimumViableUser;
    /** element that the card will be anchored to */
    anchorRef: RefObject<HTMLElement>;
    /** determines whether the card is currently open or not */
    isDetailsOpen: boolean;
    /** whether the user card should display the email or not */
    hideEmail?: boolean;
    /** whether the user card should display the email or not */
    showFollow?: boolean;
    /** callback to be executed */
    onMouseLeave?: PopoverProps['onClose'];
    /** whether the user card is loading or not */
    isLoading?: boolean;
    /** callback on mouse enter */
    onMouseEnter?: () => void;
    /** optional user block classname */
    className?: string;
    /** optional popover placement */
    placement?: Placement;
    /** whether the user card should display actions or not */
    hideActions?: boolean;
    /** whether the popover should behave as a dialog or not */
    asPopoverDialog?: boolean;
}

/**
 * Displays a user card popover, which can be used to show user details in a popover.
 * The popover can be triggered by hovering or clicking on the user avatar.
 * @param UserCardPopoverProps
 * @returns UserCardPopover
 */
export const UserCardPopover: React.FC<UserCardPopoverProps> = ({
    anchorRef,
    isDetailsOpen,
    onMouseEnter,
    onMouseLeave,
    placement = Placement.BOTTOM,
    asPopoverDialog = false,
    user,
    ...props
}) => {
    const { block, element } = useClassnames(USER_CARD_POPOVER_CLASSNAME);
    const isCurrentUserConnected = useSelector(isConnected);
    const { translateKey } = useTranslate();
    const { getUserFullName } = useUser(user);

    const fullName = getUserFullName();

    if (!isCurrentUserConnected) {
        return null;
    }

    return (
        <Popover
            anchorRef={anchorRef}
            isOpen={isDetailsOpen}
            onClose={onMouseLeave}
            offset={{ away: 8 }}
            placement={placement}
            className={block()}
            focusAnchorOnClose={asPopoverDialog}
            {...(asPopoverDialog
                ? {
                      role: 'dialog',
                      'aria-modal': 'true',
                      'aria-label': `${translateKey(GLOBAL.USER_PROFILE)}${fullName !== '' ? ` - ${fullName}` : ''}`,
                      closeOnClickAway: true,
                      closeOnEscape: true,
                      withFocusTrap: true,
                      usePortal: true,
                  }
                : {})}
        >
            <FlexBox
                className={element('container')}
                orientation={Orientation.vertical}
                onMouseLeave={onMouseLeave}
                onMouseEnter={onMouseEnter}
            >
                <Toolbar
                    className={element('toolbar')}
                    after={
                        <IconButton
                            label={translateKey(GLOBAL.CLOSE)}
                            icon={mdiClose}
                            className={element(USER_CARD_POPOVER_CLOSE_ELEMENT)}
                            emphasis={Emphasis.low}
                            onClick={onMouseLeave}
                        />
                    }
                />
                <UserCard user={user} shouldFetchUser {...props} />
            </FlexBox>
        </Popover>
    );
};

export interface UserCardPopoverTriggerProps extends IconButtonProps {
    className: string;
    user: MinimumViableUser;
}

/**
 * Displays a trigger button for the user card popover.
 * The button can be used to open the user card popover on click.
 * @param UserCardPopoverTriggerProps
 * @returns UserCardPopoverTrigger
 */
export const UserCardPopoverTrigger = React.forwardRef<HTMLButtonElement, UserCardPopoverTriggerProps>(
    (props: UserCardPopoverTriggerProps, ref) => {
        const { onClick, className, user, ...restOfProps } = props;
        const { translateKey } = useTranslate();
        const { getUserFullName } = useUser(user);

        const fullName = getUserFullName();

        return (
            <IconButton
                ref={ref}
                icon={mdiMenuDown}
                onClick={onClick}
                {...restOfProps}
                label={`${translateKey(USER.SHOW_PROFILE_PREVIEW)}${fullName !== '' ? ` - ${fullName}` : ''}`}
                emphasis="low"
                size="s"
                className={classnames(className, restOfProps.className)}
            />
        );
    },
);

export * from '../../hooks/useUserCardPopover';
